import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, LogOut, Package } from "lucide-react";

export default function SearchPage() {
  const navigate = useNavigate();
  const [orderNumber, setOrderNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [employee, setEmployee] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem("token");
    const storedEmployee = localStorage.getItem("employee");
    
    if (!token) {
      navigate("/");
      return;
    }

    if (storedEmployee) {
      setEmployee(JSON.parse(storedEmployee));
    }
  }, [navigate]);

  const handleSearch = (e) => {
    e.preventDefault();
    
    if (!orderNumber.trim()) {
      toast.error("Veuillez entrer un numéro de commande");
      return;
    }

    setLoading(true);
    
    // Navigate to result page with order number
    setTimeout(() => {
      navigate(`/result/${orderNumber.trim()}`);
      setLoading(false);
    }, 300);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("employee");
    toast.success("Déconnexion réussie");
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="flex items-center justify-between p-4 border-b-2 border-black">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#e5007c] border-2 border-black flex items-center justify-center">
            <Package className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
              Livreur
            </p>
            <p className="font-bold text-black">
              {employee ? `${employee.firstname} ${employee.lastname}` : 'Chargement...'}
            </p>
          </div>
        </div>
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="h-12 px-4 font-bold uppercase tracking-wide border-2 border-black hover:bg-gray-100"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-2" />
          <span className="hidden sm:inline">SORTIR</span>
        </Button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md animate-slide-up">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 
              className="text-3xl sm:text-4xl font-black tracking-tight uppercase text-black mb-2"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              RECHERCHER
            </h1>
            <p className="text-lg font-medium text-gray-600 uppercase tracking-wider">
              Une Commande
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="space-y-6">
            <div className="relative">
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="N° COMMANDE"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value.replace(/\D/g, ''))}
                className="h-20 text-2xl sm:text-3xl text-center font-bold uppercase tracking-wider border-2 border-black rounded-none focus-visible:ring-2 focus-visible:ring-[#e5007c] focus-visible:ring-offset-2 placeholder:text-gray-400 placeholder:text-xl"
                data-testid="search-input"
                autoComplete="off"
                autoFocus
              />
            </div>

            <Button
              type="submit"
              className="btn-primary w-full h-20 text-xl"
              disabled={loading}
              data-testid="search-submit"
            >
              {loading ? (
                <span className="flex items-center gap-3">
                  <span className="animate-spin text-2xl">⏳</span>
                  RECHERCHE...
                </span>
              ) : (
                <span className="flex items-center gap-3">
                  <Search className="w-7 h-7" />
                  RECHERCHER
                </span>
              )}
            </Button>
          </form>

          {/* Hint */}
          <p className="text-center text-sm text-gray-500 mt-6 uppercase tracking-wider">
            Entrez le numéro de commande
          </p>
        </div>
      </main>
    </div>
  );
}
